import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Image, Pencil, Plus, Search, Trash2 } from 'lucide-react';
import { otrosItemsApi } from '@/services/otrosItemsApi';
import { resolveImageUrl } from '@/utils/formatters';
import { StockBadge } from '@/components/shared/StockBadge';
import type { OtroItemCreateRequest, OtroItemDto } from '@/types/otroItem';

const QUERY_KEY = ['otros-items'] as const;
const emptyForm: OtroItemCreateRequest = {
  codigo: '',
  nombre: '',
  descripcion: '',
  unidadMedida: '',
};

export function OtrosItemsSection() {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<OtroItemCreateRequest>({ ...emptyForm });

  const [showImg, setShowImg] = useState(false);
  const [imgItem, setImgItem] = useState<OtroItemDto | null>(null);
  const [imgFile, setImgFile] = useState<File | null>(null);
  const [imgPreview, setImgPreview] = useState('');
  const imgInputRef = useRef<HTMLInputElement>(null);

  const { data: items = [], isLoading } = useQuery({
    queryKey: [...QUERY_KEY, searchTerm],
    queryFn: () => (searchTerm.trim() ? otrosItemsApi.search(searchTerm.trim()) : otrosItemsApi.findAll()),
  });

  const saveMutation = useMutation({
    mutationFn: (data: OtroItemCreateRequest) =>
      editingId ? otrosItemsApi.update(editingId, data) : otrosItemsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      setShowModal(false);
      setEditingId(null);
      setForm({ ...emptyForm });
      toast.success(editingId ? 'Item actualizado' : 'Item creado');
    },
    onError: (err: { response?: { data?: { error?: string } } }) =>
      toast.error(err.response?.data?.error ?? 'Error al guardar item'),
  });

  const deleteMutation = useMutation({
    mutationFn: otrosItemsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast.success('Item eliminado');
    },
  });

  const uploadMutation = useMutation({
    mutationFn: ({ id, formData }: { id: number; formData: FormData }) =>
      otrosItemsApi.uploadImage(id, formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      setShowImg(false);
      setImgItem(null);
      setImgFile(null);
      setImgPreview('');
      toast.success('Imagen actualizada');
    },
    onError: () => toast.error('Error al subir imagen'),
  });

  const openEdit = (it: OtroItemDto) => {
    setEditingId(it.id);
    setForm({
      codigo: it.codigo,
      nombre: it.nombre,
      descripcion: it.descripcion ?? '',
      unidadMedida: it.unidadMedida,
    });
    setShowModal(true);
  };

  const openImg = (it: OtroItemDto) => {
    setImgItem(it);
    setImgFile(null);
    setImgPreview(resolveImageUrl(it.imagen) ?? '');
    setShowImg(true);
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-6 mt-12">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Otros Items (platinas, pernos, eslingas, etc.)</h2>
        <div className="flex gap-2">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm w-44"
            />
          </div>
          <button
            onClick={() => {
              setEditingId(null);
              setForm({ ...emptyForm });
              setShowModal(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
          >
            <Plus size={16} />
            Nuevo Item
          </button>
        </div>
      </div>

      <div className="hidden md:block bg-white rounded-xl border border-gray-200 overflow-hidden mb-4">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Imagen</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Código</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Nombre</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Unidad</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Stock</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={6} className="text-center py-8 text-gray-500">Cargando...</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-8 text-gray-500">No hay items</td></tr>
            ) : (
              items.map((it) => (
                <tr key={it.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3">
                    {it.imagen ? (
                      <img src={resolveImageUrl(it.imagen)!} alt={it.nombre} className="w-10 h-10 object-cover rounded-lg border border-gray-200" />
                    ) : (
                      <div className="w-10 h-10 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center"><Image size={16} className="text-gray-400" /></div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm font-mono">{it.codigo}</td>
                  <td className="px-4 py-3">
                    <p className="text-sm font-medium">{it.nombre}</p>
                    {it.descripcion && <p className="text-xs text-gray-500 truncate max-w-xs">{it.descripcion}</p>}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{it.unidadMedida}</td>
                  <td className="px-4 py-3"><StockBadge cantidad={it.cantidadTotal} desglose={it.stockPorAlmacen} /></td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button onClick={() => openImg(it)} className="text-purple-500 hover:text-purple-700" title="Imagen"><Image size={16} /></button>
                      <button onClick={() => openEdit(it)} className="text-blue-600 hover:text-blue-800"><Pencil size={16} /></button>
                      <button onClick={() => deleteMutation.mutate(it.id)} className="text-red-500 hover:text-red-700"><Trash2 size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="md:hidden space-y-3 mb-4">
        {isLoading ? (
          <p className="text-center py-8 text-gray-500">Cargando...</p>
        ) : items.length === 0 ? (
          <p className="text-center py-8 text-gray-500">No hay items</p>
        ) : (
          items.map((it) => (
            <div key={it.id} className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex gap-3 items-start">
                {it.imagen ? (
                  <img src={resolveImageUrl(it.imagen)!} alt={it.nombre} className="w-12 h-12 object-cover rounded-lg border" />
                ) : (
                  <div className="w-12 h-12 bg-gray-100 rounded-lg border flex items-center justify-center"><Image size={18} className="text-gray-400" /></div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{it.nombre}</p>
                  <p className="text-xs text-gray-500 font-mono">{it.codigo} · {it.unidadMedida}</p>
                  {it.descripcion && <p className="text-xs text-gray-600 truncate">{it.descripcion}</p>}
                  <div className="mt-1"><StockBadge cantidad={it.cantidadTotal} desglose={it.stockPorAlmacen} /></div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => openImg(it)} className="text-purple-500"><Image size={16} /></button>
                  <button onClick={() => openEdit(it)} className="text-blue-600"><Pencil size={16} /></button>
                  <button onClick={() => deleteMutation.mutate(it.id)} className="text-red-500"><Trash2 size={16} /></button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50">
          <div className="bg-white rounded-t-xl sm:rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto p-4 sm:p-6">
            <h3 className="text-lg font-semibold mb-4">{editingId ? 'Editar Item' : 'Nuevo Item'}</h3>
            <form onSubmit={(e) => { e.preventDefault(); saveMutation.mutate(form); }} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Código
                  {!editingId && <span className="text-xs text-gray-400 font-normal ml-1">(opcional — se autogenera)</span>}
                </label>
                <input
                  type="text"
                  value={form.codigo ?? ''}
                  onChange={(e) => setForm({ ...form, codigo: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={editingId ? '' : 'Ej: PRN-0001 o vacío para autogenerar'}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
                <input
                  type="text"
                  value={form.nombre}
                  onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
                <textarea
                  value={form.descripcion ?? ''}
                  onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                  rows={2}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Unidad de medida</label>
                <input
                  type="text"
                  value={form.unidadMedida ?? ''}
                  onChange={(e) => setForm({ ...form, unidadMedida: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="UNIDAD, METRO, KG, etc."
                />
              </div>
              <div className="flex gap-3 justify-end pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Cancelar</button>
                <button type="submit" disabled={saveMutation.isPending} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50">
                  {saveMutation.isPending ? 'Guardando...' : editingId ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showImg && imgItem && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50">
          <div className="bg-white rounded-t-xl sm:rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-4 sm:p-6">
            <h3 className="text-lg font-semibold mb-1">Imagen del Item</h3>
            <p className="text-sm text-gray-500 mb-5">{imgItem.nombre}</p>
            <form onSubmit={(e) => {
              e.preventDefault();
              if (!imgFile) { toast.error('Selecciona una imagen'); return; }
              const fd = new FormData();
              fd.append('imagen', imgFile);
              uploadMutation.mutate({ id: imgItem.id, formData: fd });
            }} className="space-y-5">
              <div className="border-2 border-dashed border-gray-300 rounded-xl p-4 flex flex-col items-center gap-3 cursor-pointer hover:border-blue-400 transition-colors" onClick={() => imgInputRef.current?.click()}>
                {imgPreview ? (
                  <img src={imgPreview} alt="Preview" className="w-full max-h-40 object-contain rounded-lg" />
                ) : (
                  <><Image size={32} className="text-gray-300" /><span className="text-sm text-gray-400">Haz clic para seleccionar imagen</span></>
                )}
                {imgPreview && <span className="text-xs text-blue-600 hover:underline">Cambiar imagen</span>}
              </div>
              <input ref={imgInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) { setImgFile(f); setImgPreview(URL.createObjectURL(f)); }
              }} />
              <div className="flex gap-3 justify-end pt-1">
                <button type="button" onClick={() => setShowImg(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Cancelar</button>
                <button type="submit" disabled={uploadMutation.isPending || !imgFile} className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 disabled:opacity-50">
                  {uploadMutation.isPending ? 'Subiendo...' : 'Guardar imagen'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
